
import React, { useState, useEffect } from 'react';

import { Outlet } from 'react-router-dom';
import { Avatar } from '../../fieldsComponents/appointments/avatar';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../fieldsComponents/appointments/dropdown';
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from '../../fieldsComponents/appointments/navbar';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from '../../fieldsComponents/appointments/sidebar';
import { SidebarLayout } from '../../fieldsComponents/appointments/sidebar-layout';
import { Combobox, ComboboxOption, ComboboxLabel } from '../../fieldsComponents/appointments/combobox';
import { ChevronUpIcon, PlusIcon, UserIcon, ArrowRightStartOnRectangleIcon, CalendarDaysIcon, BuildingOfficeIcon } from '@heroicons/react/16/solid';
import { Cog6ToothIcon } from '@heroicons/react/20/solid';
import GoogleCalendarPanel from './googleCalendarPannel';
import JobAdderLogo from '../../images/jobAdder/jobAdderLogo.svg';
import { useNavigate } from 'react-router-dom';
import ProfileDialog from '../../components/appointments/profileDialog';
import LoadingMask from '../../components/appointments/loadingMask';

import axios from 'axios';

import AccountVerificationDialog from '../../components/appointments/createAccountDialog';

import { appendAccountToUrl, getAccountFromUrl, updateUrlWithAccount } from '../../utils/appointments/urlUtils';
import { getAcctNoFromLocalStorage, setAcctNoInLocalStorage } from '../../utils/appointments/accountContext';
import { validateAccountAccess } from '../../utils/appointments/accountContext';
import { Link, useLocation } from 'react-router-dom';


// AccessIssueWithRedirect component: shows access issue and redirects after 5 seconds
function AccessIssueWithRedirect({ message, comboOptions }) {
  const [count, setCount] = React.useState(5);
  const [redirected, setRedirected] = React.useState(false);
  const [buttonEnabled, setButtonEnabled] = React.useState(false);


  React.useEffect(() => {
    if (count > 0) {
      const timer = setTimeout(() => setCount(c => c - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // Find the first real account (not 'Create New Account')
      const firstAccount = comboOptions.find(opt => !opt.isNewAccount);
      if (firstAccount && firstAccount.acctNo) {
        updateUrlWithAccount(firstAccount.acctNo);
        window.location.reload();
        setRedirected(true);
      } else {
        setButtonEnabled(true);
      }
    }
  }, [count, comboOptions]);

  // If redirect fails (user still on this page after 5s), enable button
  React.useEffect(() => {
    if (count === 0 && !redirected) {
      setButtonEnabled(true);
    }
  }, [count, redirected]);

  const handleManualGo = () => {
    // Try to go to first accessible account, else fallback to /appointments
    const firstAccount = comboOptions.find(opt => !opt.isNewAccount);
    if (firstAccount && firstAccount.acctNo) {
      updateUrlWithAccount(firstAccount.acctNo);
      window.location.reload();
    } else {
      window.location.href = '/appointments';
    }
  };

  return (
    <div className="fixed inset-0 bg-red-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4 flex flex-col items-center">
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 17a2 2 0 002-2v-2a2 2 0 00-2-2 2 2 0 00-2 2v2a2 2 0 002 2zm6-6V9a6 6 0 10-12 0v2a2 2 0 00-2 2v6a2 2 0 002 2h12a2 2 0 002-2v-6a2 2 0 00-2-2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
          Account Access Issue
        </h3>
        <p className="text-sm text-gray-600 text-center mb-4">
          {message}
        </p>
        <div style={{ fontSize: '2rem', color: '#dc2626', fontFamily: 'monospace', textAlign: 'center', marginBottom: '0.5rem' }}>
          Redirecting in {count}...
        </div>
        {/* Show button only after countdown and redirect fails */}
        {buttonEnabled && count === 0 && (
          <button
            className="mt-2 px-6 py-2 rounded bg-black text-white font-semibold transition-colors hover:bg-gray-800"
            onClick={handleManualGo}
          >
            Go to My Application
          </button>
        )}
      </div>
    </div>
  );
}

function Dashboard({ children }) {
  // Track if account state (including URL) is fully resolved
  const [isAccountStateResolved, setIsAccountStateResolved] = useState(false);
  const location = useLocation();
  const [acctId, _setAcctId] = useState('');
  const [acctIdLoading, setAcctIdLoading] = useState(true);
  // Debug wrapper for setAcctId
  const setAcctId = (val) => {
    if (val === null || val === '') {
      setAcctIdLoading(true);
      _setAcctId('');
      return;
    }
    _setAcctId(val);
    setAcctIdLoading(false);
  };
  const [acctNo, setAcctNo] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [acctName, setAcctName] = useState('User');
  const [userName, setUserName] = useState('');
  const [phone, setPhone] = useState('');
  const [userId, setUserId] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState(''); // Default placeholder image
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isVerificationDialogOpen, setIsVerificationDialogOpen] = useState(false);
  const [isCreatingNewAccount, setIsCreatingNewAccount] = useState(false); // Track if creating new account vs verifying existing
  // State to trigger specific UI updates without causing full re-renders
  const [accountsRefreshKey, setAccountsRefreshKey] = useState(0); // Specific for account-related updates
  const [userRefreshKey, setUserRefreshKey] = useState(0); // Specific for user-related updates
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0); // Specific for calendar-related updates
  const [uiRefreshKey, setUiRefreshKey] = useState(0); // For general UI updates when needed
  const [isPanelLoading, setIsPanelLoading] = useState(false);
  // Debug: log every time isPanelLoading changes

  // Debug: wrap setIsPanelLoading to log calls
  const debugSetIsPanelLoading = (val) => {

    setIsPanelLoading(val);
  };
  const [isSuperAdmin, setIsSuperAdmin] = useState(false); // Track if user is admin

  // --- Synchronous accounts initialization for instant combo box update ---
  // Accounts state: fetched from backend only once and reused
  const [accounts, setAccounts] = useState([]); // Store user's accounts
  const [selectedAccount, setSelectedAccount] = useState(null); // Currently selected account
  const [isAccountLinked, setIsAccountLinked] = useState(true); // Track if user has linked an account
  // Delay state for empty account message
  const [showNoAccountMessage, setShowNoAccountMessage] = useState(false);
  const [accountError, setAccountError] = useState(null); // Track account loading errors
  const [accountErrorType, setAccountErrorType] = useState(null); // Track the type of error (access_denied, not_found, etc.)
  const [isLoading, setIsLoading] = useState(true); // Track loading state
  const navigate = useNavigate();

  // Combo box loading state and delayed options
  const [comboOptionsLoading, setComboOptionsLoading] = useState(true);
  const [comboOptions, setComboOptions] = useState([]);



  // TO FETCH USER DATA


  /**
   * Fetches user data from MongoDB based on userId
   * @param {string} userId - The user ID to fetch data for
   * @returns {Promise<Object|null>} - User data object or null if not found/error
   * 
   */
  const fetchUserData = async (userId) => {
    if (!userId || userId === 'undefined' || userId === 'null') {
      return null;
    }
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('userEmail');
    const timezone = localStorage.getItem('timezone');

    let role = undefined;
    let roleLabel = undefined;
    try {
      const token = localStorage.getItem('token');
      if (token && userId) {
        const response = await axios.get(`/api/ui/appointment/gCal/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          role = response.data.role;
          roleLabel = response.data.roleLabel;
        }
      }
    } catch (error) {
      console.warn('Failed to fetch user role/roleLabel:', error);
    }
    const userData = {
      name: userName,
      phone: phone,
      email: userEmail,
      timezone: timezone,
      role: role,
      roleLabel: roleLabel
    };
    return userData;
  };

  // TO CHECK USER SUPER ADMIN OR NOT

  const checkSuperAdminAccess = (userData) => {
    // If no data provided, grant access (user not found case)
    if (!userData) {
      return true;
    }

    if (userData.roleLabel && userData.roleLabel.toLowerCase() === 'admin') {
      return false;
    }

    if (userData.roleLabel && userData.roleLabel.toLowerCase() === 'superadmin') {
      return true;
    }
    return false;
  };


  // Merged useEffect: initialize dashboard, check account in URL/localStorage, and load user data
  useEffect(() => {
    const init = async () => {
      // Account in URL/localStorage logic
      const urlAcctNo = getAccountFromUrl(location.search);
      const storedAcctNo = getAcctNoFromLocalStorage();
      if (!urlAcctNo && storedAcctNo) {
        updateUrlWithAccount(storedAcctNo);
        window.location.reload();
        return;
      }
      await initializeDashboard();
      setIsAccountStateResolved(true);

      // Load user data from MongoDB
      const userIdValue = localStorage.getItem('userId');
      try {
        const userData = await fetchUserData(userIdValue);
        if (userData) {
          const hasSuperAdminAccess = checkSuperAdminAccess(userData);
          setIsSuperAdmin(hasSuperAdminAccess);
          if (userData.name) {
            setUserName(userData.name);
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setIsSuperAdmin(true);
      }
    };
    init();
  }, []);



  // Centralized account fetching
  // Centralized account fetching (only called once)
  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const userIdValue = localStorage.getItem('userId');
      if (!token || !userIdValue) return [];
      const response = await axios.get(`/api/ui/appointment/gCal/accountName/${userIdValue}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.success && Array.isArray(response.data.accounts)) {
        return response.data.accounts;
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
    return [];
  };

  // Load accounts on mount or refresh
  useEffect(() => {
    const loadAccounts = async () => {
      setIsLoading(true);
      setAcctIdLoading(true);
      const accountsList = await fetchAccounts();
      setAccounts(accountsList);
      let urlAcctNo = getAccountFromUrl(location.search);
      let storedAcctNo = getAcctNoFromLocalStorage();
      let targetAcctNo = urlAcctNo || storedAcctNo;
      let resolvedAccount = accountsList.find(acc => acc.acctNo === targetAcctNo || acc.accountNo === targetAcctNo);
      if (resolvedAccount) {
        setAcctId(resolvedAccount.acctId);
        setAcctNo(resolvedAccount.acctNo);
        setAcctName(resolvedAccount.accountName || resolvedAccount.acctName || resolvedAccount.name || '');
      } else if (accountsList.length > 0) {
        // Fallback to first account
        const firstAccount = accountsList[0];
        setAcctId(firstAccount.acctId);
        setAcctNo(firstAccount.acctNo);
        setAcctName(firstAccount.accountName || firstAccount.acctName || firstAccount.name || '');
      } else {
        setAcctId('');
        setAcctNo('');
        setAcctName('');
      }
      setAcctIdLoading(false);
      setIsLoading(false);
    };
    loadAccounts();
    // Get userId from localStorage
    const userIdValue = localStorage.getItem('userId');
    setUserId(userIdValue);
    // Get user email, acctName, userName, phone, and profileImageUrl from localStorage
    const storedEmail = localStorage.getItem('userEmail');
    const storedUserName = localStorage.getItem('userName');
    const storedProfileImageUrl = localStorage.getItem('profileImageUrl');
    if (storedEmail) setUserEmail(storedEmail);
    if (storedUserName) {
      setUserName(storedUserName);
      setUserRefreshKey(prev => prev + 1);
    }
    if (storedProfileImageUrl) setProfileImageUrl(storedProfileImageUrl);
    // Add an event listener for username updates
    const handleUserNameUpdated = (event) => {
      const newUserName = event.detail.userName;
      setUserName(newUserName);
      setUserRefreshKey(prev => prev + 1);
    };
    window.addEventListener('userNameUpdated', handleUserNameUpdated);
    return () => {
      window.removeEventListener('userNameUpdated', handleUserNameUpdated);
    };
  }, [accountsRefreshKey]);




  // Merged effect: listen for localStorage changes, storage events, and accountVerified
  useEffect(() => {
    // Only set up listeners once on mount
    const checkProfileImage = () => {
      const currentStoredImage = localStorage.getItem('profileImageUrl');
      if (currentStoredImage && currentStoredImage !== profileImageUrl) {
        setProfileImageUrl(currentStoredImage);
      }
    };
    const intervalId = setInterval(checkProfileImage, 5000);

    const handleStorageChange = () => checkProfileImage();
    window.addEventListener('storage', handleStorageChange);

    const handleCreateAccount = (event) => {
      if (event.detail && event.detail.accountName) {
        setAcctName(event.detail.accountName);
      }
      try {
        if (Array.isArray(accounts)) {
          // Only update if accounts actually changed
          setAccounts(prev => {
            if (prev === accounts) return prev;
            return [...accounts];
          });
          if (event.detail && event.detail.account) {
            const createdAccount = accounts.find(acc =>
              acc.acctId === event.detail.account.acctId ||
              acc.acctNo === event.detail.account.acctNo
            );
            if (createdAccount) {
              setSelectedAccount({ ...createdAccount });
            }
          }
        }
      } catch (error) {
        console.error('Error updating accounts during creation:', error);
      }
    };
    window.addEventListener('accountCreated', handleCreateAccount);

    // Also listen for custom profileImageUpdated and phoneUpdated events
    const handleProfileImageUpdated = (event) => {
      if (event.detail && event.detail.url) {
        if (event.detail.url !== profileImageUrl) {
          setProfileImageUrl(event.detail.url);
          setUserRefreshKey(prev => prev + 1);
        }
      }
    };
    window.addEventListener('profileImageUpdated', handleProfileImageUpdated);

    const handlePhoneUpdated = (event) => {
      if (event.detail && event.detail.phone) {
        setPhone(event.detail.phone);
        setUserRefreshKey(prev => prev + 1);
      }
    };
    window.addEventListener('phoneUpdated', handlePhoneUpdated);

    // Listen for userNameUpdated event
    const handleUserNameUpdated = (event) => {
      const newUserName = event.detail.userName;
      if (newUserName !== userName) {
        setUserName(newUserName);
        setUserRefreshKey(prev => prev + 1);
      }
    };
    window.addEventListener('userNameUpdated', handleUserNameUpdated);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profileImageUpdated', handleProfileImageUpdated);
      window.removeEventListener('phoneUpdated', handlePhoneUpdated);
      window.removeEventListener('userNameUpdated', handleUserNameUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleSignOut = async (e) => {
    e.preventDefault();

    const clearData = async () => {
      try {
        if (window.gapi && window.gapi.auth2) {
          const auth2 = window.gapi.auth2.getAuthInstance();
          if (auth2) {
            await auth2.signOut();
            await auth2.disconnect();
          }
        }
      } catch (err) {
        console.warn('Google Calendar sign-out failed:', err);
      }
      sessionStorage.clear();
      localStorage.clear()
      window.dispatchEvent(new Event('authChange'));
    };

    try {
      await clearData();

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 50);
    } catch (error) {
      console.error('Error during sign out:', error);
      await clearData();
      navigate('/login', { replace: true });
    }
  };

  const handleOpenProfileDialog = (e) => {
    if (e) e.preventDefault();
    // Set profile dialog to open
    setIsProfileDialogOpen(true);
    // Headless UI's Menu component will automatically close when a MenuButton is clicked
  };
  const handleCloseProfileDialog = () => {
    setIsProfileDialogOpen(false);
  };

  const handleUpdateUserName = (newUserName) => {
    // Update the userName state
    setUserName(newUserName);
    // Update user-related UI components
    setUserRefreshKey(prev => prev + 1);
  };
  // Callback to update the profile image in the dashboard
  const updateProfileImage = (newImageUrl) => {
    if (!newImageUrl) {
      return;
    }
    // Store the original URL without any cache busting
    const originalUrl = newImageUrl.split('?')[0];

    // Update state with the original URL - we'll add cache busting in the render
    setProfileImageUrl(originalUrl);

    // Store original URL in localStorage
    localStorage.setItem('profileImageUrl', originalUrl);

    // Update UI for profile image changes
    setUserRefreshKey(prev => prev + 1);
  };

  // Callback to update the user email in the dashboard
  const updateUserEmail = (newEmail) => {
    setUserEmail(newEmail);
    localStorage.setItem('userEmail', newEmail); // Persist the new email in localStorage
    // Update UI for user-related changes
    setUserRefreshKey(prev => prev + 1);
  };

  // Callback to update the phone number in the dashboard
  const updatePhone = (newPhone) => {
    setPhone(newPhone);
    // Removed localStorage usage for phone
    // Update UI for user-related changes
    setUserRefreshKey(prev => prev + 1);
  };

  // Handle opening the account verification dialog
  const handleOpenVerificationDialog = (isNewAccount = false) => {
    // Set a flag to indicate we're creating a new account vs verifying existing
    setIsCreatingNewAccount(isNewAccount);
    setIsVerificationDialogOpen(true);
  };

  // Handle closing the account verification dialog
  const handleCloseVerificationDialog = () => {
    setIsVerificationDialogOpen(false);
    // Reset the creation flag when closing the dialog
    setIsCreatingNewAccount(false);
  };

  // Handle saving the account verification data
  const handleCreateAccount = (formData) => {

    if (formData && formData.account && formData.account.acctNo) {
      try {
        const account = formData.account;
        setAcctNo(account.acctNo);
        setAcctName(account.name);
        if (account.timezone) {
          localStorage.setItem('timezone', account.timezone);
        }
        let accountsList = Array.isArray(accounts) ? [...accounts] : [];
        const newAcctId = account.acctId || `acc_${Date.now()}`;
        const newAccount = {
          acctId: newAcctId,
          acctNo: account.acctNo,
          accountName: account.name,
          canCreateCalendar: true,
          role: 'Super Admin',
          calendars: [],
          timezone: account.timezone || ''
        };

        const existingAccountIndex = accountsList.findIndex(acc => acc.acctNo === account.acctNo);
        let updatedAccountsList = [...accountsList];
        if (existingAccountIndex >= 0) {
          updatedAccountsList[existingAccountIndex] = {
            ...updatedAccountsList[existingAccountIndex],
            ...newAccount
          };
        } else {
          updatedAccountsList.push(newAccount);
        }
        setAccounts([...updatedAccountsList]);
        setIsAccountLinked(true);
        setIsVerificationDialogOpen(false);
        localStorage.setItem('isConnected', 'false');
        setAccountsRefreshKey(prev => prev + 1);
        // Backend is instant, update URL and reload immediately
        updateUrlWithAccount(account.acctNo);
        window.location.reload();
        // Force ComboBox to select the new account after state update
        setTimeout(() => {
          const comboMatch = comboOptions.find(opt => opt.acctNo === account.acctNo);
          if (comboMatch) {
            setSelectedAccount(comboMatch);
          }
        }, 200);

        const accountCreatedEvent = new CustomEvent('accountCreated', {
          detail: {
            account: newAccount,
            accountName: account.name
          }
        });

        window.dispatchEvent(accountCreatedEvent);
        setAcctName(account.name);
      } catch (error) {
        console.error('Error saving account verification data:', error);
      }
    }
  };

  /**
   * Initialize the dashboard by loading account data
   */


  const initializeDashboard = async () => {
    setIsLoading(true);
    setAccountError(null);
    setIsVerificationDialogOpen(false);
    try {
      // Use utility functions for account selection and validation
      const urlAccountNo = getAccountFromUrl(location.search);
      const storedAcctNo = getAcctNoFromLocalStorage();
      // Prefer URL, then localStorage, then first account
      const targetAcctNo = urlAccountNo || storedAcctNo;
      // Check account existence in backend
      const token = localStorage.getItem('token');
      const acctIdResult = localStorage.getItem('acctId') || '';
      if (!acctIdResult) {
        // Debug log: backend response and token value

        setAccountError('No Account Linked');
        setAccountErrorType('no-account');
        setIsAccountLinked(false);
        setIsVerificationDialogOpen(false);
        setIsLoading(false);
        return;
      }
      // Continue with normal account loading
      const result = await LoadAccount(targetAcctNo);
      if (result.success) {
        setIsAccountLinked(true);
        setIsVerificationDialogOpen(false);
        setAccountError(null);
        // If no acctNo in URL, update URL with the first account
        if (!urlAccountNo && result.account && result.account.acctNo) {
          updateUrlWithAccount(result.account.acctNo);
        }
      } else if (result.error === 'no_accounts') {
        setIsAccountLinked(false);
        setIsVerificationDialogOpen(false);
        setAccountError(null);
      } else {
        // Debug log: backend response and token value for error

        setIsAccountLinked(true);
        setIsVerificationDialogOpen(false);
        setAccountError(result.error);
      }
    } catch (err) {

      setAccountError('Failed to initialize dashboard');
      setIsAccountLinked(false);
      setIsVerificationDialogOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Simplified LoadAccount function ---
  // LoadAccount now uses accounts from state, not a second API call
  const LoadAccount = async (targetAccountNo = null) => {
    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      if (!token || !userId) {
        navigate('/login');
        return { success: false, error: 'Not logged in' };
      }
      // Use accounts from state
      let accountsList = accounts;
      if (!Array.isArray(accountsList) || accountsList.length === 0) {
        return { success: false, error: 'no_accounts' };
      }
      // 1. Try URL acctNo, 2. first account from backend
      let accountNo = targetAccountNo || accountsList[0]?.acctNo || accountsList[0]?.accountNo;
      if (!accountNo) {
        return { success: false, error: 'no_accounts' };
      }
      const account = accountsList.find(acc => acc.acctNo === accountNo || acc.accountNo === accountNo);
      if (!account) {
        return { success: false, error: `Sorry, you do not have permission to access this account or the account does not exist.` };
      }
      // Use utility function for backend access validation
      const hasAccess = await validateAccountAccess(accountNo, token);
      if (!hasAccess) {
        return { success: false, error: `Sorry, you do not have permission to access this account or the account does not exist.` };
      }
      setAcctNo(accountNo);
      setAccountError(null);
      return { success: true, account };
    } catch (error) {
      return { success: false, error: error.message || 'Unknown error' };
    }
  };

  // Combo box options: derived from accounts state
  useEffect(() => {
    setComboOptionsLoading(true);
    const options = accounts.map(acc => ({
      acctId: acc.acctId || acc.acctNo || '',
      acctNo: acc.acctNo || '',
      accountName: acc.accountName || '',
    }));
    options.push({ acctId: 'new_account', accountName: 'Create New Account', isNewAccount: true });
    setComboOptions(options);
    setComboOptionsLoading(false);
  }, [accounts]);



  // --- Auto-switch to default account after access error ---
  useEffect(() => {
    if (
      accountError &&
      typeof accountError === 'string' &&
      accountError.toLowerCase().includes('permission') &&
      accounts.length > 0
    ) {
      // Show error for 3 seconds, then switch to first accessible account
      const timer = setTimeout(() => {
        // Find the first accessible account (not the 'Create New Account' option)
        const firstAccount = accounts.find(acc => acc.acctId && acc.acctId !== 'new_account');
        if (firstAccount) {
          // Update URL and reload (no localStorage)
          updateUrlWithAccount(firstAccount.acctNo || firstAccount.accountNo);
          window.location.reload();
        } else {
          // No accessible accounts, show No Account Linked panel
          setIsAccountLinked(false);
          setAccountError(null);
        }
      }, 3000); // 3 seconds
      return () => clearTimeout(timer);
    }
  }, [accountError, accounts]);


  // --- Centralized account and user initialization on page load ---
  useEffect(() => {
    // --- Account page load logic ---
    const token = localStorage.getItem('token');
    // If not logged in, redirect
    if (!token) {
      const urlAcctNo = getAccountFromUrl(location.search);
      const loginUrl = urlAcctNo ? `/login?acc=${urlAcctNo}` : '/login';
      navigate(loginUrl, { replace: true });
      return;
    }
    // User is logged in
    const urlAcctNo = getAccountFromUrl(location.search);
    if (urlAcctNo && accounts.length > 0) {
      // If acctNo in URL, check if it exists in accounts
      const found = accounts.find(acc => acc.acctNo === urlAcctNo || acc.accountNo === urlAcctNo);
      if (!found) {
        // Not in combo box, check backend
        setAccountError(null);
        setAccountErrorType(null);
        (async () => {
          try {
            const resp = await fetch('/api/ui/appointment/gCal/checkAccountByNo', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ acctNo: urlAcctNo })
            });
            const data = await resp.json();
            if (resp.ok && data.success && data.account) {
              setAccountError('You do not have permission to access this account.');
              setAccountErrorType('access-issue');
            } else {
              setAccountError('No Account Linked');
              setAccountErrorType('no-account');
              setAccounts([]); // Clear accounts to trigger No Account Linked UI
            }
          } catch {
            setAccountError('No Account Linked');
            setAccountErrorType('no-account');
            setAccounts([]); // Clear accounts to trigger No Account Linked UI
          }
        })();
      } else {
        setIsAccountLinked(true);
        setIsVerificationDialogOpen(false);
        setAccountError(null);
        setAccountErrorType(null);
      }
    } else if (!urlAcctNo && accounts.length > 0) {
      // No acctNo in URL, use first account
      const firstAccount = accounts[0];
      if (firstAccount && firstAccount.acctNo) {
        updateUrlWithAccount(firstAccount.acctNo);
      }
    } else if (accounts.length === 0) {
      setIsAccountLinked(false);
      setAccountError(null);
    }
  }, [location.search, accounts]);

  // Combo box options: derived from accounts state only
  useEffect(() => {
    setComboOptionsLoading(true);
    const options = accounts.map(acc => ({
      acctId: acc.acctId || acc.acctNo || '',
      acctNo: acc.acctNo || '',
      accountName: acc.accountName || acc.acctName || acc.name || '',
      acctName: acc.acctName || acc.accountName || acc.name || '',
      name: acc.name || acc.accountName || acc.acctName || '',
    }));
    options.push({ acctId: 'new_account', acctName: 'Create New Account', accountName: 'Create New Account', isNewAccount: true });
    setComboOptions(options);
    setComboOptionsLoading(false);
  }, [accounts]);

  // Prevent infinite update loop: only set selectedAccount if it is not already set and there are real accounts
  useEffect(() => {
    if (acctIdLoading) return;
    const urlAcctNo = getAccountFromUrl(location.search);
    const match = comboOptions.find(opt => opt.acctNo === urlAcctNo);
    if (
      urlAcctNo &&
      match &&
      (!selectedAccount || selectedAccount.acctNo !== urlAcctNo)
    ) {
      if (
        match &&
        match.acctNo &&
        (!selectedAccount || selectedAccount.acctNo !== match.acctNo)
      ) {
        setSelectedAccount(match);
      }
      if (
        match &&
        match.acctNo &&
        acctNo !== match.acctNo &&
        typeof match.acctNo !== 'undefined' &&
        match.acctNo !== null
      ) {
        setAcctNo(match.acctNo);
      }
      if (
        match &&
        match.acctId &&
        acctId !== match.acctId &&
        typeof match.acctId !== 'undefined' &&
        match.acctId !== null
      ) {
        setAcctId(match.acctId);
      }
      if (match && !match.isNewAccount && match.acctNo && match.acctId) {
        setAcctNoInLocalStorage(match.acctNo, match.acctId);
      }
    } else if (
      !urlAcctNo &&
      !selectedAccount &&
      comboOptions.length > 0
    ) {
      const storedAcctNo = getAcctNoFromLocalStorage();
      const storedMatch = comboOptions.find(opt => opt.acctNo === storedAcctNo);
      if (
        storedAcctNo &&
        storedMatch &&
        storedMatch.acctNo &&
        (!selectedAccount || selectedAccount.acctNo !== storedMatch.acctNo)
      ) {
        setSelectedAccount(storedMatch);
      }
      if (
        storedMatch &&
        storedMatch.acctNo &&
        acctNo !== storedMatch.acctNo &&
        typeof storedMatch.acctNo !== 'undefined' &&
        storedMatch.acctNo !== null
      ) {
        setAcctNo(storedMatch.acctNo);
      }
      if (
        storedMatch &&
        storedMatch.acctId &&
        acctId !== storedMatch.acctId &&
        typeof storedMatch.acctId !== 'undefined' &&
        storedMatch.acctId !== null
      ) {
        setAcctId(storedMatch.acctId);
      }
      if (storedAcctNo && storedMatch && storedMatch.acctNo && storedMatch.acctId) {
        updateUrlWithAccount(storedAcctNo);
      }
      if (!storedMatch) {
        const firstRealAccount = comboOptions.find(opt => !opt.isNewAccount);
        if (
          firstRealAccount &&
          firstRealAccount.acctNo &&
          (!selectedAccount || selectedAccount.acctNo !== firstRealAccount.acctNo)
        ) {
          setSelectedAccount(firstRealAccount);
        }
        if (
          firstRealAccount &&
          firstRealAccount.acctNo &&
          acctNo !== firstRealAccount.acctNo &&
          typeof firstRealAccount.acctNo !== 'undefined' &&
          firstRealAccount.acctNo !== null
        ) {
          setAcctNo(firstRealAccount.acctNo);
        }
        if (
          firstRealAccount &&
          firstRealAccount.acctId &&
          acctId !== firstRealAccount.acctId &&
          typeof firstRealAccount.acctId !== 'undefined' &&
          firstRealAccount.acctId !== null
        ) {
          setAcctId(firstRealAccount.acctId);
        }
        if (
          firstRealAccount &&
          firstRealAccount.acctNo &&
          firstRealAccount.acctId
        ) {
          updateUrlWithAccount(firstRealAccount.acctNo);
          setAcctNoInLocalStorage(firstRealAccount.acctNo, firstRealAccount.acctId);
        }
      }
    }
  }, [comboOptions, location.search, acctNo, acctId, selectedAccount, acctIdLoading]);




  const hasRealAccounts = comboOptions.some(opt => !opt.isNewAccount);

  // Debug: Log isSuperAdmin value when it changes
  useEffect(() => {
    if (typeof isSuperAdmin === 'undefined') {
      console.warn('[Warning] isSuperAdmin is undefined! Forcing to false.');
      setIsSuperAdmin(false);
    }
  }, [isSuperAdmin]);


  return (
    <div>


      {/* Account error state: Only show if user has accounts but tried to access an account they do not own */}
      {accountError && accounts.length > 0 && accountErrorType === 'access-issue' && (
        <AccessIssueWithRedirect message={accountError} comboOptions={comboOptions} />
      )}
      {/* Show 'No Account Linked' message inside dashboard layout, not as a full replacement */}
      {/* Only show No Account Linked if not loading, no real accounts, and error type is 'no-account' */}
      {/* Only show No Account Linked if not loading, no real accounts, error type is 'no-account', and URL is resolved */}

      {accountErrorType === 'no-account' && !isLoading && accounts.length === 0 && isAccountStateResolved && (
        <>
          <SidebarLayout
            navbar={
              <Navbar>
                <NavbarSpacer />
                <NavbarSection>
                  <Dropdown>
                    <DropdownButton as={NavbarItem} className="cursor-pointer">
                      <div className="relative mb-5 w-14 h-14 rounded-full overflow-hidden">
                        <Avatar
                          key={`navbar-avatar-${userRefreshKey}`}
                          src={`${profileImageUrl}${profileImageUrl?.includes('?') ? '' : `?cb=${userRefreshKey}`}`}
                          alt="Profile"
                          onError={(e) => {
                            e.target.src = "/profile.png";
                          }}
                        />
                      </div>
                    </DropdownButton>
                    <DropdownMenu className="min-w-64" anchor="bottom end">
                      <DropdownItem onClick={handleOpenProfileDialog} className="cursor-pointer">
                        <UserIcon />
                        <DropdownLabel>My profile</DropdownLabel>
                      </DropdownItem>
                      <DropdownItem onClick={handleSignOut} className="cursor-pointer">
                        <ArrowRightStartOnRectangleIcon />
                        <DropdownLabel>Sign out</DropdownLabel>
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </NavbarSection>
              </Navbar>
            }
            sidebar={
              <Sidebar>
                <SidebarHeader>
                  <Dropdown>
                    <DropdownButton as={SidebarItem} className="lg:mb-2.5">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                        <Avatar src={`/${process.env.REACT_APP_BRAND_NAME || 'Botamation'}.svg`} className="avatar-fit" />
                      </div>
                      <SidebarLabel>{process.env.REACT_APP_BRAND_NAME || 'Botamation'} HUB</SidebarLabel>
                    </DropdownButton>
                  </Dropdown>
                  {/* Account Selection Dropdown - Always show */}
                  <div className="mt-2 px-3 relative z-50">
                    <Combobox
                      as="div"
                      key={`accounts-combobox-${accountsRefreshKey}`}
                      options={comboOptions}
                      value={selectedAccount}
                      onChange={(selected) => {
                        if (selected && selected.isNewAccount) {
                          handleOpenVerificationDialog(true);
                          return;
                        }
                        if (selected && selected.acctNo) {
                          updateUrlWithAccount(selected.acctNo);
                          setAcctNoInLocalStorage(selected.acctNo, selected.acctId);
                          window.location.reload();
                        }
                      }}
                      placeholder={comboOptionsLoading ? 'Loading accounts...' : 'Select Account'}
                      displayValue={(account) => {
                        if (!account) return comboOptionsLoading ? 'Loading accounts...' : 'Select Account';
                        return (
                          account.accountName ||
                          account.acctName ||
                          account.name ||
                          (userEmail ? userEmail.split('@')[0] : 'Unnamed Account')
                        );
                      }}
                      className="w-full max-w-[320px]"
                      filter={(account, query) => {
                        if (account?.isNewAccount) return false;
                        if (!account) return false;
                        const searchableName = account.accountName || '';
                        return searchableName.toLowerCase().includes(query.toLowerCase());
                      }}
                      loading={comboOptionsLoading ? true : undefined}

                      dropdownClassName=" min-w-[200px] max-w-[260px] bg-white"
                    >
                      {(option) => (
                        <ComboboxOption
                          key={`${option.acctId}-${accountsRefreshKey}`}
                          value={option}
                          className={(option.isNewAccount ? 'border-t border-gray-200 mt-1 pt-1 ' : '') + 'w-full'}
                        >
                          <ComboboxLabel>
                            {option.isNewAccount ? (
                              <span className="flex items-center group-hover/option:text-white">
                                <PlusIcon className="w-4 h-4 mr-1" />
                                {'Create New Account'}
                              </span>
                            ) : (
                              <span className="group-hover/option:text-white">
                                {option.accountName}
                              </span>
                            )}
                          </ComboboxLabel>
                        </ComboboxOption>
                      )}
                    </Combobox>
                  </div>
                </SidebarHeader>
                <SidebarBody>
                  <SidebarSection>
                    <Link to={appendAccountToUrl('/appointments', getAccountFromUrl(location.search))} className="no-underline">
                      <SidebarItem>
                        <span className="flex items-center gap-1">
                          <div className="relative w-8 h-8 flex items-center justify-center">
                            {/* Modern gradient background with shadow */}
                            <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 rounded-lg shadow-lg"></div>
                            {/* Icon with better sizing and color */}
                            <CalendarDaysIcon className="relative w-5 h-5 text-white drop-shadow-sm" />
                            {/* Premium glow effect */}
                            <div className="absolute -inset-0.5 bg-gradient-to-br from-emerald-400/40 via-teal-500/40 to-cyan-600/40 rounded-lg blur opacity-75 -z-10"></div>
                          </div>
                          <SidebarLabel className={`cursor-pointer${location.pathname.startsWith('/appointments') && !location.pathname.startsWith('/appointments/settings') ? ' font-bold' : ''}`}>Appointments</SidebarLabel>
                          {location.pathname.startsWith('/appointments') && !location.pathname.startsWith('/appointments/settings') && (
                            <span className="absolute" style={{ right: 0, top: 0, height: '100%', width: '2px', background: '#10b981', borderRadius: '0 2px 2px 0' }}></span>
                          )}
                        </span>
                      </SidebarItem>
                    </Link>

                    {/* Hotel Booking Sidebar Item */}
                    <Link to={appendAccountToUrl('/hotelBooking', getAccountFromUrl(location.search))} className="no-underline">
                      <SidebarItem>
                        <span className="flex items-center gap-1">
                          <div className="relative w-8 h-8 flex items-center justify-center">
                            {/* Modern gradient background with shadow */}
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 rounded-lg shadow-lg"></div>
                            {/* Icon with better sizing and color */}
                            <BuildingOfficeIcon className="relative w-5 h-5 text-white drop-shadow-sm" />
                            {/* Premium glow effect */}
                            <div className="absolute -inset-0.5 bg-gradient-to-br from-purple-400/40 via-pink-500/40 to-red-500/40 rounded-lg blur opacity-75 -z-10"></div>
                          </div>
                          <SidebarLabel className={`cursor-pointer${location.pathname.startsWith('/hotelBooking') ? ' font-bold' : ''}`}>Bookings</SidebarLabel>
                          {location.pathname.startsWith('/hotelBooking') && (
                            <span className="absolute" style={{ right: 0, top: 0, height: '100%', width: '2px', background: '#ec4899', borderRadius: '0 2px 2px 0' }}></span>
                          )}
                        </span>
                      </SidebarItem>
                    </Link>

                  </SidebarSection>            <SidebarSpacer />            {/* Add Settings Item - only visible for super admin users (role=0) */}
                  {isSuperAdmin && (
                    <SidebarSection>
                      {(() => {
                        // Highlight if on any settings route
                        const isSettingsActive = location.pathname.includes('/appointments/settings');
                        return (
                          <Link
                            to={appendAccountToUrl('/appointments/settings/users', getAccountFromUrl(location.search))}
                            className="no-underline"
                            key={`settings-item-${accountsRefreshKey}`}
                          >
                            <SidebarItem className={isSettingsActive ? 'bg-black text-white rounded-md cursor-pointer' : 'cursor-pointer'}>
                              <Cog6ToothIcon className="w-5 h-5" />
                              <SidebarLabel className="cursor-pointer">Settings</SidebarLabel>
                            </SidebarItem>
                          </Link>
                        );
                      })()}
                    </SidebarSection>
                  )}
                </SidebarBody>
                <SidebarFooter className="max-lg:hidden">            <Dropdown>
                  <DropdownButton as={SidebarItem} className="cursor-pointer">
                    <span className="flex min-w-0 items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden ">
                        {/* Use a stable cache-busting parameter tied to refresh counter */}
                        <Avatar
                          key={`sidebar-avatar-${userRefreshKey}`}
                          src={`${profileImageUrl}${profileImageUrl?.includes('?') ? '' : `?cb=${userRefreshKey}`}`}
                          className="avatar-fit"
                          alt="Profile"
                          onError={(e) => {
                            e.target.src = "/profile.png"; // Fallback to local image
                          }}
                        />
                      </div>                    <span className="min-w-0">
                        <span
                          key={`username-display-${userRefreshKey}`}
                          className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white cursor-pointer"
                        >
                          {/* Display user's name instead of account name - use state directly */}
                          {userName || userEmail?.split('@')[0] || 'User'}
                        </span>
                        <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400 cursor-pointer">
                          {userEmail || 'No email available'}
                        </span>
                      </span>
                    </span>
                    <ChevronUpIcon />
                  </DropdownButton>
                  <DropdownMenu className="min-w-64" anchor="top start">
                    <DropdownItem onClick={handleOpenProfileDialog}>
                      <UserIcon />
                      <DropdownLabel>My profile</DropdownLabel>
                    </DropdownItem>
                    <DropdownItem onClick={handleSignOut}>
                      <ArrowRightStartOnRectangleIcon />
                      <DropdownLabel>Sign out</DropdownLabel>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                </SidebarFooter>
              </Sidebar>
            }
          >
            {/* Main dashboard area: show the message in place of GoogleCalendarPanel */}
            <div className="flex flex-col items-center justify-center min-h-[80vh] h-full w-full bg-gray-50 p-4">
              <div className="text-center max-w-md w-full p-8 bg-white rounded-lg shadow-lg">
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <div className="relative w-16 h-16 flex items-center justify-center">
                    {/* Modern gradient background with shadow */}
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 rounded-xl shadow-lg"></div>
                    {/* Icon with better sizing and color */}
                    <CalendarDaysIcon className="relative w-10 h-10 text-white drop-shadow-sm" />
                    {/* Premium glow effect */}
                    <div className="absolute -inset-0.5 bg-gradient-to-br from-emerald-400/40 via-teal-500/40 to-cyan-600/40 rounded-xl blur opacity-75 -z-10"></div>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">No Account Linked</h2>
                <p className="text-gray-600 mb-8">
                  You need to link your Botamation account to start using the Appointment system.
                </p>
                <button
                  onClick={() => handleOpenVerificationDialog(true)}
                  className="w-full bg-black hover:bg-gray-800 text-white py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition-colors"
                >
                  Link Account
                </button>
              </div>
            </div>
          </SidebarLayout>
          {/* Always render ProfileDialog so it can open regardless of account state */}
          <ProfileDialog
            isOpen={isProfileDialogOpen}
            onClose={handleCloseProfileDialog}
            acctNo={acctNo}
            acctId={acctId}
            userId={userId}
            userEmail={userEmail}
            acctName={acctName}
            userName={userName}
            phone={phone}
            profileImageUrl={profileImageUrl}
            onUpdateProfileImage={updateProfileImage}
            onUpdateUserEmail={updateUserEmail}
            onUpdateUserName={handleUpdateUserName}
            onUpdatePhone={updatePhone}
          />
        </>
      )}


      {/* Main Dashboard - only show if no loading/error states and user has at least one account, or show No Account Linked panel if not linked */}
      {/* Only show main dashboard if not loading, no account error, and there is at least one real account */}
      {!isLoading && !accountError && accounts.length > 0 && (
        <SidebarLayout
          navbar={
            <Navbar>
              <NavbarSpacer />
              <NavbarSection>            <Dropdown>
                <DropdownButton as={NavbarItem} className="cursor-pointer">
                  <div className="relative mb-5 w-14 h-14 rounded-full overflow-hidden">
                    {/* Use a stable cache-busting parameter tied to refresh counter */}
                    <Avatar
                      key={`navbar-avatar-${userRefreshKey}`}
                      src={`${profileImageUrl}${profileImageUrl?.includes('?') ? '' : `?cb=${userRefreshKey}`}`}
                      alt="Profile"
                      onError={(e) => {
                        e.target.src = "/profile.png"; // Fallback to local image
                      }}
                    />
                  </div>
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="bottom end">
                  <DropdownItem onClick={handleOpenProfileDialog} className="cursor-pointer">
                    <UserIcon />
                    <DropdownLabel>My profile</DropdownLabel>
                  </DropdownItem>
                  <DropdownItem onClick={handleSignOut} className="cursor-pointer">
                    <ArrowRightStartOnRectangleIcon />
                    <DropdownLabel>Sign out</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
              </NavbarSection>
            </Navbar>
          }
          sidebar={
            <Sidebar>
              <SidebarHeader>
                <Dropdown>
                  <DropdownButton as={SidebarItem} className="lg:mb-2.5">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                      <Avatar src={`/${process.env.REACT_APP_BRAND_NAME || 'Botamation'}.svg`} className="avatar-fit" />
                    </div>
                    <SidebarLabel>{process.env.REACT_APP_BRAND_NAME || 'Application Hub'}</SidebarLabel>
                  </DropdownButton>
                </Dropdown>

                {/* Account Selection Dropdown - Always show */}
                <div className="mt-2 px-3 relative z-50">

                  <Combobox
                    as="div"
                    key={`accounts-combobox-${accountsRefreshKey}`}
                    options={comboOptions}
                    value={selectedAccount}
                    onChange={(selected) => {
                      if (selected && selected.isNewAccount) {
                        handleOpenVerificationDialog(true);
                        return;
                      }
                      if (selected && selected.acctNo) {
                        updateUrlWithAccount(selected.acctNo);
                        setAcctNoInLocalStorage(selected.acctNo, selected.acctId);
                        window.location.reload();
                      }
                    }}
                    placeholder={comboOptionsLoading ? 'Loading accounts...' : 'Select Account'}
                    displayValue={(account) => {
                      if (!account) return comboOptionsLoading ? 'Loading accounts...' : 'Select Account';
                      return account.accountName || (userEmail ? userEmail.split('@')[0] : 'Unnamed Account');
                    }}
                    className="w-full"
                    dropdownClassName=" min-w-[200px] max-w-[260px] bg-white"
                    filter={(account, query) => {
                      if (account?.isNewAccount) return false;
                      if (!account) return false;
                      const searchableName = account.accountName || '';
                      return searchableName.toLowerCase().includes(query.toLowerCase());
                    }}
                    loading={comboOptionsLoading ? true : undefined}

                  >
                    {(option) => (
                      <ComboboxOption
                        key={`${option.acctId}-${accountsRefreshKey}`}
                        value={option}
                        className={option.isNewAccount ? 'border-t border-gray-200 mt-1 pt-1' : ''}
                      >
                        <ComboboxLabel>
                          {option.isNewAccount ? (
                            <span className="flex items-center group-hover/option:text-white">

                              <PlusIcon className="w-4 h-4 mr-1" />
                              {option.accountName}
                            </span>
                          ) : (
                            <span className="group-hover/option:text-white">
                              {option.accountName}
                            </span>
                          )}
                        </ComboboxLabel>
                      </ComboboxOption>
                    )}
                  </Combobox>

                </div>
              </SidebarHeader>
              <SidebarBody>
                <SidebarSection>

                  <Link to={appendAccountToUrl('/appointments', getAccountFromUrl(location.search))} className="no-underline">
                    <SidebarItem>
                      <span className="flex items-center gap-3">
                        <div className="relative w-7 h-7 flex items-center justify-center">
                          {/* Modern gradient background with shadow */}
                          <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 rounded-lg shadow-lg"></div>
                          {/* Icon with better sizing and color */}
                          <CalendarDaysIcon className="relative w-4 h-4 text-white drop-shadow-sm" />
                          {/* Premium glow effect */}
                          <div className="absolute -inset-0.5 bg-gradient-to-br from-emerald-400/40 via-teal-500/40 to-cyan-600/40 rounded-lg blur opacity-75 -z-10"></div>
                        </div>
                        <SidebarLabel className={`cursor-pointer${location.pathname.startsWith('/appointments') && !location.pathname.startsWith('/appointments/settings') ? ' font-bold' : ''}`}>Appointments</SidebarLabel>
                        {location.pathname.startsWith('/appointments') && !location.pathname.startsWith('/appointments/settings') && (
                          <span className="absolute" style={{ right: 0, top: '50%', transform: 'translateY(-50%)' }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <polygon points="4,3 12,8 4,13" fill="#000" />
                            </svg>
                          </span>
                        )}
                      </span>
                    </SidebarItem>
                  </Link>

                  {/* Hotel Booking Sidebar Item */}
                  <Link to={appendAccountToUrl('/hotelBooking', getAccountFromUrl(location.search))} className="no-underline">
                    <SidebarItem>
                      <span className="flex items-center gap-3">
                        <div className="relative w-7 h-7 flex items-center justify-center">
                          {/* Modern gradient background with shadow */}
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 rounded-lg shadow-lg"></div>
                          {/* Icon with better sizing and color */}
                          <BuildingOfficeIcon className="relative w-4 h-4 text-white drop-shadow-sm" />
                          {/* Premium glow effect */}
                          <div className="absolute -inset-0.5 bg-gradient-to-br from-purple-400/40 via-pink-500/40 to-red-500/40 rounded-lg blur opacity-75 -z-10"></div>
                        </div>
                        <SidebarLabel className={`cursor-pointer${location.pathname.startsWith('/hotelBooking') ? ' font-bold' : ''}`}>Bookings</SidebarLabel>
                        {location.pathname.startsWith('/hotelBooking') && (
                          <span className="absolute" style={{ right: 0, top: '50%', transform: 'translateY(-50%)' }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <polygon points="4,3 12,8 4,13" fill="#000" />
                            </svg>
                          </span>
                        )}
                      </span>
                    </SidebarItem>
                  </Link>

                </SidebarSection>            <SidebarSpacer />            {/* Add Settings Item - only visible for super admin users (role=0) */}
                {isSuperAdmin && (
                  <SidebarSection>
                    {(() => {
                      // Highlight if on any settings route
                      const isSettingsActive = location.pathname.includes('/appointments/settings');
                      return (
                        <Link
                          to={appendAccountToUrl('/appointments/settings/users', getAccountFromUrl(location.search))}
                          className="no-underline"
                          key={`settings-item-${accountsRefreshKey}`}
                        >
                          <SidebarItem className={isSettingsActive ? 'bg-black text-white rounded-md cursor-pointer' : 'cursor-pointer'}>
                            <Cog6ToothIcon className="w-5 h-5" />
                            <SidebarLabel className="cursor-pointer">Settings</SidebarLabel>
                          </SidebarItem>
                        </Link>
                      );
                    })()}
                  </SidebarSection>
                )}
              </SidebarBody>
              <SidebarFooter className="max-lg:hidden">            <Dropdown>
                <DropdownButton as={SidebarItem} className="cursor-pointer">
                  <span className="flex min-w-0 items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden ">
                      {/* Use a stable cache-busting parameter tied to refresh counter */}
                      <Avatar
                        key={`sidebar-avatar-${userRefreshKey}`}
                        src={`${profileImageUrl}${profileImageUrl?.includes('?') ? '' : `?cb=${userRefreshKey}`}`}
                        className="avatar-fit"
                        alt="Profile"
                        onError={(e) => {
                          e.target.src = "/profile.png"; // Fallback to local image
                        }}
                      />
                    </div>                    <span className="min-w-0">
                      <span
                        key={`username-display-${userRefreshKey}`}
                        className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white cursor-pointer"
                      >
                        {/* Display user's name instead of account name - use state directly */}
                        {userName || userEmail?.split('@')[0] || 'User'}
                      </span>
                      <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400 cursor-pointer">
                        {userEmail || 'No email available'}
                      </span>
                    </span>
                  </span>
                  <ChevronUpIcon />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="top start">
                  <DropdownItem onClick={handleOpenProfileDialog} className="cursor-pointer">
                    <UserIcon />
                    <DropdownLabel>My profile</DropdownLabel>
                  </DropdownItem>
                  <DropdownItem onClick={handleSignOut} className="cursor-pointer">
                    <ArrowRightStartOnRectangleIcon />
                    <DropdownLabel>Sign out</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
              </SidebarFooter>
            </Sidebar>
          }
        >
          {/* Main content area logic */}
          <div className="relative w-full h-full flex-1 min-h-[800px]">

            {/* Panel loading mask removed. Use skeletons/placeholders if needed. */}
            {hasRealAccounts ? (
              <>

                <Outlet context={{ setIsPanelLoading: debugSetIsPanelLoading, isPanelLoading, acctId, setAcctId }} />
              </>
            )
              : null}
          </div>


          {/* Profile Dialog */}
          <ProfileDialog
            isOpen={isProfileDialogOpen}
            onClose={handleCloseProfileDialog}
            acctNo={acctNo}
            acctId={acctId}
            userId={userId}
            userEmail={userEmail}
            acctName={acctName}
            userName={userName}
            phone={phone}
            profileImageUrl={profileImageUrl}
            onUpdateProfileImage={updateProfileImage}
            onUpdateUserEmail={updateUserEmail}
            onUpdateUserName={handleUpdateUserName}
            onUpdatePhone={updatePhone}
          />
        </SidebarLayout>)}

      {/* Account Verification Dialog: show as modal only if user clicks Link Account (always render at root) */}
      {isVerificationDialogOpen && (
        <AccountVerificationDialog
          isOpen={true}
          onClose={handleCloseVerificationDialog}
          onSave={handleCreateAccount}
          accountData={{ acctNo: '' }}
          isNewAccount={true}
        />
      )}
    </div>
  );
}
export default Dashboard;

